import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
  Banknote, CheckCircle2, XCircle, FileDown, ListChecks, PackageCheck,
  Building2, Filter, Plus, UserPlus, Upload, Trash2,
} from "lucide-react";

const fmtINR = (n) => (n ? "₹" + Number(n).toLocaleString("en-IN") : "—");

const STATUS_BADGES = {
  pending_hr: { label: "Pending HR", cls: "bg-amber-100 text-amber-800" },
  pending_senior: { label: "Pending Senior Mgmt", cls: "bg-blue-100 text-blue-700" },
  pending_disbursement: { label: "Pending Payment", cls: "bg-purple-100 text-purple-700" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
};

export default function AdvanceApprovalQueue() {
  const { user } = useAuth();
  const role = user?.role;

  // Determine which tabs to show
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const isHR = ["hr", "hr_admin", "hr_maker", "super_admin"].includes(role);
  const isSenior = role === "senior_management" || role === "super_admin";
  const isAccounts = ["accounts", "super_admin", "unit_manager", "hr", "hr_admin", "hr_maker", "senior_management"].includes(role);

  const tabs = useMemo(() => {
    const t = [];
    if (isHR) t.push({ key: "hr_queue", label: "HR Queue" });
    if (isSenior) t.push({ key: "senior_queue", label: "Senior Mgmt Queue" });
    if (isAccounts) t.push({ key: "accounts_queue", label: "Accounts Disbursement" });
    t.push({ key: "all", label: "All Advances" });
    return t;
  }, [isHR, isSenior, isAccounts]);

  const [tab, setTab] = useState(tabs[0]?.key || "all");
  const [advances, setAdvances] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Action dialogs
  const [actionDialog, setActionDialog] = useState(null);
  // { type: 'hr_approve'|'hr_reject'|'sr_approve'|'sr_reject'|'disburse', advance: {...} }
  const [actionForm, setActionForm] = useState({});
  const [busy, setBusy] = useState(false);

  // Admin-create dialogs (HR/Unit Manager/Accounts can create on behalf)
  const canAdminCreate = ["super_admin", "hr", "hr_admin", "hr_maker", "unit_manager", "accounts"].includes(role);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [pendingPayrollMonth, setPendingPayrollMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [createForm, setCreateForm] = useState({
    employee_id: "",
    amount: "",
    reason_category: "",
    reason_description: "",
    repayment_months: 1,
    payment_mode: "Cash",
    notes: "",
    override_max_check: false,
    deduction_start_month: "",  // populated by effect below
  });
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkDeductionMonth, setBulkDeductionMonth] = useState("");
  const [bulkResult, setBulkResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: adv }, { data: u }] = await Promise.all([
        api.get(`/advances?scope=${tab}`),
        api.get("/units").catch(() => ({ data: [] })),
      ]);
      setAdvances(adv || []);
      setUnits(u || []);
    } catch (e) {
      toast.error("Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tab]);

  // Fetch oldest draft payroll month (inline in dialog open handler below)

  // Load employees + config once on mount
  useEffect(() => {
    if (!canAdminCreate) return;
    Promise.all([
      api.get("/employees?limit=2000").then(({ data }) => {
        const items = Array.isArray(data) ? data : (data.items || []);
        setEmployees(items.filter((e) => e.status === "Working"));
      }).catch(() => {}),
      api.get("/advances/config").then(({ data }) => {
        setReasonOptions(data.reason_options || []);
      }).catch(() => {}),
    ]);
    // eslint-disable-next-line
  }, [canAdminCreate]);

  const openAction = (type, advance) => {
    setActionDialog({ type, advance });
    // Compute default deduction month = pending payroll month (oldest draft)
    // best-effort; fallback to current month
    const now = new Date();
    const fallback = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    setActionForm({
      approved_amount: advance.amount_requested,
      remarks: "",
      payment_unit_id: advance.payment_unit_id || (units[0]?.id || ""),
      payment_mode: advance.payment_mode || "Cash",
      transaction_ref: "",
      deduction_start_month: advance.deduction_start_month || fallback,
    });
    if (type === "disburse") {
      // Try to fetch pending payroll month asynchronously
      api.get("/payroll/runs", { params: { status: "draft", limit: 1 } })
        .then((r) => {
          const run = (r.data || [])[0];
          if (run?.month) {
            const m = String(run.month).slice(0, 7);
            setActionForm((f) => ({ ...f, deduction_start_month: m }));
          }
        })
        .catch(() => {});
    }
  };

  const submitAction = async () => {
    const { type, advance } = actionDialog;
    if ((actionForm.remarks || "").length < 5 && type !== "disburse" && type !== "shift_month") {
      toast.error("Remarks (min 5 chars)");
      return;
    }
    setBusy(true);
    try {
      if (type === "hr_approve") {
        await api.post(`/advances/${advance.id}/hr-decision`, {
          decision: "approve",
          approved_amount: parseFloat(actionForm.approved_amount) || advance.amount_requested,
          remarks: actionForm.remarks,
        });
      } else if (type === "hr_reject") {
        await api.post(`/advances/${advance.id}/hr-decision`, {
          decision: "reject",
          remarks: actionForm.remarks,
        });
      } else if (type === "sr_approve") {
        await api.post(`/advances/${advance.id}/senior-decision`, {
          decision: "approve",
          remarks: actionForm.remarks,
        });
      } else if (type === "sr_reject") {
        await api.post(`/advances/${advance.id}/senior-decision`, {
          decision: "reject",
          remarks: actionForm.remarks,
        });
      } else if (type === "disburse") {
        // Unit Manager — payment_unit_id is forced server-side to their own unit
        if (role !== "unit_manager" && !actionForm.payment_unit_id) {
          toast.error("Select payment unit");
          return;
        }
        await api.post(`/advances/${advance.id}/disburse`, {
          decision: "disburse",
          payment_unit_id: actionForm.payment_unit_id || user?.unit_id || null,
          payment_mode: actionForm.payment_mode,
          transaction_ref: actionForm.transaction_ref || null,
          reduced_amount: actionForm.reduced_amount ? parseFloat(actionForm.reduced_amount) : null,
          remarks: actionForm.remarks || null,
          deduction_start_month: actionForm.deduction_start_month || null,
        });
      } else if (type === "accounts_refuse") {
        if (!(actionForm.remarks || "").trim() || actionForm.remarks.trim().length < 5) {
          toast.error("Refusal remarks (min 5 chars) required");
          return;
        }
        await api.post(`/advances/${advance.id}/disburse`, {
          decision: "refuse",
          remarks: actionForm.remarks,
        });
      } else if (type === "shift_month") {
        if (!actionForm.deduction_start_month) {
          toast.error("Pick a new month"); return;
        }
        if ((actionForm.remarks || "").trim().length < 3) {
          toast.error("Reason required (min 3 chars)"); return;
        }
        await api.post(`/advances/${advance.id}/shift-month`, {
          new_month: actionForm.deduction_start_month,
          reason: actionForm.remarks,
        });
      }
      toast.success("Done");
      setActionDialog(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="advance-approvals">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Banknote className="h-3 w-3" /> HR Tools
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            💰 Advance Management
          </h1>
        </div>
        {canAdminCreate && (
          <>
            <Button
              onClick={() => { setShowBulkDialog(true); setBulkRows([{ employee_id: "", amount: "", reason_category: "", reason_description: "", repayment_months: 1, payment_mode: "Cash" }]); setBulkResult(null); }}
              variant="outline"
              size="sm"
              data-testid="bulk-create-btn"
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk Create
            </Button>
            <Button
              onClick={async () => {
                try {
                  const { data } = await api.get(
                    "/payroll/runs", { params: { status: "draft", limit: 50 } });
                  const runs = Array.isArray(data) ? data
                                 : (data.items || data.runs || []);
                  const drafts = runs
                    .filter((r) => r.status === "draft" && r.month)
                    .map((r) => String(r.month).slice(0, 7))
                    .sort();
                  const d = new Date();
                  const fb = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                  const chosen = drafts[0] || fb;
                  setPendingPayrollMonth(chosen);
                  setCreateForm((f) => ({ ...f, deduction_start_month: chosen }));
                  setBulkDeductionMonth(chosen);
                } catch (_) { /* keep current default */ }
                setShowCreateDialog(true);
              }}
              className="brand-bg brand-bg-hover text-white"
              size="sm"
              data-testid="admin-create-btn"
            >
              <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Create for Employee
            </Button>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white border border-slate-200 rounded-lg p-1 flex gap-1 overflow-x-auto" data-testid="tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded text-xs font-semibold whitespace-nowrap ${tab === t.key ? "brand-bg text-white" : "text-slate-600 hover:bg-slate-50"}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : advances.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            <ListChecks className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No advances in this queue.</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-600">
                <th className="px-3 py-2 font-semibold">Request #</th>
                <th className="px-3 py-2 font-semibold">Employee</th>
                <th className="px-3 py-2 font-semibold text-right">Requested</th>
                <th className="px-3 py-2 font-semibold text-right">Approved</th>
                <th className="px-3 py-2 font-semibold">Reason</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {advances.map((a) => {
                const badge = STATUS_BADGES[a.overall_status] || { label: a.overall_status, cls: "bg-slate-100" };
                return (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`row-${a.id}`}>
                    <td className="px-3 py-1.5 font-mono text-[10px]">
                      <div>{a.request_number}</div>
                      {a.voucher_number && (
                        <a href={`/api/advances/${a.id}/voucher-pdf`} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline text-[9px]">
                          {a.voucher_number}
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="font-semibold">{a.employee_name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{a.employee_punch_id}</div>
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtINR(a.amount_requested)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold">{a.amount_approved ? fmtINR(a.amount_approved) : "—"}</td>
                    <td className="px-3 py-1.5 text-slate-600 max-w-[260px] whitespace-normal break-words align-top" title={a.reason_description}>
                      <div className="font-semibold text-[10px]">{a.reason_category}</div>
                      <div className="text-[10px] text-slate-500 italic whitespace-normal break-words">{a.reason_description}</div>
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
                      {a.repayment_months > 1 && <div className="text-[9px] text-slate-500 mt-0.5">{a.repayment_months} EMIs</div>}
                      {a.deduction_start_month && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[9px] text-blue-700">↓ from {a.deduction_start_month}</span>
                          {isHR && a.overall_status === "paid" && (
                            <button
                              onClick={() => openAction("shift_month", a)}
                              className="text-[9px] underline text-bravura-700 hover:text-bravura-800"
                              title="Change EMI start month (HR / Super Admin only)"
                              data-testid={`shift-month-${a.id}`}>
                              edit
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex gap-1 flex-wrap">
                        {a.overall_status === "pending_hr" && isHR && (
                          <>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => openAction("hr_approve", a)} data-testid={`hr-approve-${a.id}`}>
                              <CheckCircle2 className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-300 text-red-700 hover:bg-red-50" onClick={() => openAction("hr_reject", a)} data-testid={`hr-reject-${a.id}`}>
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {a.overall_status === "pending_senior" && isSenior && (
                          <>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => openAction("sr_approve", a)} data-testid={`sr-approve-${a.id}`}>
                              <CheckCircle2 className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-300 text-red-700 hover:bg-red-50" onClick={() => openAction("sr_reject", a)} data-testid={`sr-reject-${a.id}`}>
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {a.overall_status === "pending_disbursement" && isAccounts && (
                          <>
                            <Button size="sm" className="h-6 text-[10px] brand-bg brand-bg-hover text-white" onClick={() => openAction("disburse", a)} data-testid={`disburse-${a.id}`}>
                              <PackageCheck className="h-3 w-3 mr-1" /> Disburse
                            </Button>
                            <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-300 text-red-700 hover:bg-red-50" onClick={() => openAction("accounts_refuse", a)} data-testid={`refuse-${a.id}`} title="Refuse disbursement">
                              <XCircle className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {a.voucher_number && (
                          <a href={`/api/advances/${a.id}/voucher-pdf`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost" className="h-6 text-[10px]" data-testid={`pdf-${a.id}`}>
                              <FileDown className="h-3 w-3" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Action dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(v) => !v && setActionDialog(null)}>
        <DialogContent data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.type === "hr_approve" && "HR Approve Advance"}
              {actionDialog?.type === "hr_reject" && "HR Reject Advance"}
              {actionDialog?.type === "sr_approve" && "Senior Mgmt Approve"}
              {actionDialog?.type === "sr_reject" && "Senior Mgmt Reject"}
              {actionDialog?.type === "disburse" && "Disburse Payment"}
              {actionDialog?.type === "accounts_refuse" && "Refuse Disbursement"}
              {actionDialog?.type === "shift_month" && "Change Deduction Start Month"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Review the advance request and confirm your action. For Accounts, you may also refuse or disburse a reduced amount.
            </DialogDescription>
          </DialogHeader>
          {actionDialog && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                <b>{actionDialog.advance.employee_name}</b> ({actionDialog.advance.employee_punch_id}) · {actionDialog.advance.request_number}
                <br />
                Requested: <b>{fmtINR(actionDialog.advance.amount_requested)}</b>
                {actionDialog.advance.amount_approved && <> · Approved: <b>{fmtINR(actionDialog.advance.amount_approved)}</b></>}
                <br />
                <span className="text-slate-500 italic">&quot;{actionDialog.advance.reason_description}&quot;</span>
              </div>

              {actionDialog.type === "hr_approve" && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">Approved Amount (₹)</Label>
                  <Input
                    type="number"
                    step="100"
                    value={actionForm.approved_amount}
                    onChange={(e) => setActionForm({ ...actionForm, approved_amount: e.target.value })}
                    className="mt-1"
                    data-testid="approve-amount-input"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">
                    If &gt; ₹5,000, will be forwarded to Senior Mgmt.
                  </div>
                </div>
              )}

              {actionDialog.type === "shift_month" && (
                <div className="bg-blue-50 border border-blue-200 rounded p-2.5 space-y-2">
                  <div className="text-[11px] text-blue-900">
                    Current start month: <b>{actionDialog.advance.deduction_start_month || "—"}</b>
                  </div>
                  <Label className="text-[11px] uppercase tracking-wider text-blue-700">
                    New Start Month
                  </Label>
                  <Input type="month" value={actionForm.deduction_start_month || ""}
                          onChange={(e) => setActionForm({ ...actionForm,
                                                            deduction_start_month: e.target.value })}
                          className="max-w-[200px]"
                          data-testid="shift-month-input" />
                  <div className="flex gap-1 flex-wrap">
                    {(() => {
                      const chips = [];
                      const now = new Date();
                      for (let off = -2; off <= 2; off++) {
                        const d = new Date(now.getFullYear(), now.getMonth() + off, 1);
                        const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                        const lbl = d.toLocaleDateString("en-IN",
                          { month: "short", year: "2-digit" });
                        chips.push({ value: m, label: lbl });
                      }
                      return chips.map((c) => (
                        <button key={c.value} type="button"
                          onClick={() => setActionForm({ ...actionForm,
                                                          deduction_start_month: c.value })}
                          className={`text-[10px] px-1.5 py-0.5 rounded ring-1 ${
                            actionForm.deduction_start_month === c.value
                              ? "bg-blue-600 text-white ring-blue-600"
                              : "bg-white ring-slate-200 hover:bg-slate-50"
                          }`}>
                          {c.label}
                        </button>
                      ));
                    })()}
                  </div>
                  <div className="text-[10px] text-slate-600">
                    All <b>scheduled</b> EMIs of this advance will move forward starting from the chosen month. Already-deducted EMIs stay untouched.
                  </div>
                </div>
              )}

              {actionDialog.type === "disburse" && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                        Payment Unit {role !== "unit_manager" && "*"}
                      </Label>
                      {role === "unit_manager" ? (
                        <div className="mt-1 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-700">
                          Auto-set to your unit
                          <span className="ml-1 text-[10px] text-slate-400">(scoped)</span>
                        </div>
                      ) : (
                        <Select value={actionForm.payment_unit_id} onValueChange={(v) => setActionForm({ ...actionForm, payment_unit_id: v })}>
                          <SelectTrigger className="mt-1" data-testid="unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                          <SelectContent>
                            {units.map((u) => (
                              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wider text-slate-500">Payment Mode</Label>
                      <Select value={actionForm.payment_mode} onValueChange={(v) => setActionForm({ ...actionForm, payment_mode: v })}>
                        <SelectTrigger className="mt-1" data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cash">Cash</SelectItem>
                          <SelectItem value="Bank">Bank Transfer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">Transaction Ref (cheque #, UTR…)</Label>
                    <Input
                      value={actionForm.transaction_ref}
                      onChange={(e) => setActionForm({ ...actionForm, transaction_ref: e.target.value })}
                      placeholder="Optional"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                      Start Deducting From
                    </Label>
                    <Select value={actionForm.deduction_start_month || ""}
                            onValueChange={(v) => setActionForm({ ...actionForm, deduction_start_month: v })}>
                      <SelectTrigger className="mt-1"
                                       data-testid="deduction-start-month-select">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const opts = [];
                          const now = new Date();
                          // last 2 months + current + next 6 months
                          for (let off = -2; off <= 6; off++) {
                            const d = new Date(now.getFullYear(), now.getMonth() + off, 1);
                            const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                            const label = d.toLocaleDateString("en-IN",
                              { month: "long", year: "numeric" });
                            opts.push({ value: m, label });
                          }
                          return opts.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                              {o.value === actionForm.deduction_start_month && " ✓"}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                    <div className="text-[10px] text-slate-500 mt-1">
                      Default: pending payroll month. First EMI will deduct from this month&apos;s payroll.
                    </div>
                  </div>
                  <div className="border-t border-amber-200 pt-2 mt-2 bg-amber-50/40 -mx-1 px-2 pb-2 rounded">
                    <Label className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">
                      Disburse Reduced Amount (Optional)
                    </Label>
                    <Input
                      type="number"
                      step="100"
                      min="1"
                      max={actionDialog.advance.amount_approved}
                      value={actionForm.reduced_amount || ""}
                      onChange={(e) => setActionForm({ ...actionForm, reduced_amount: e.target.value })}
                      placeholder={`Approved: ₹${Number(actionDialog.advance.amount_approved || 0).toLocaleString("en-IN")}. Leave blank to disburse full amount.`}
                      className="mt-1 border-amber-300 focus-visible:ring-amber-400"
                      data-testid="reduced-amount-input"
                    />
                    <div className="text-[10px] text-amber-700 mt-1">
                      If you want to disburse less than the approved amount (e.g. ₹500 instead of ₹1,000), enter it here. Must be &le; approved amount.
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Remarks {(actionDialog.type !== "disburse") && "*"}</Label>
                <Textarea
                  value={actionForm.remarks}
                  onChange={(e) => setActionForm({ ...actionForm, remarks: e.target.value })}
                  rows={2}
                  placeholder={
                    actionDialog.type === "accounts_refuse"
                      ? "Reason for refusal (min 5 chars)"
                      : actionDialog.type === "hr_reject" || actionDialog.type === "sr_reject"
                        ? "Reason for rejection (min 5 chars)"
                        : actionDialog.type === "shift_month"
                          ? "Reason for changing deduction month (min 3 chars)"
                          : "Optional remarks"
                  }
                  className="mt-1"
                  data-testid="remarks-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button onClick={submitAction} disabled={busy} className="brand-bg brand-bg-hover text-white" data-testid="confirm-action-btn">
              {busy ? "Processing…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Create Dialog (individual) */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg" data-testid="admin-create-dialog">
          <DialogHeader>
            <DialogTitle>Create Advance on Behalf of Employee</DialogTitle>
            <DialogDescription className="sr-only">
              Admin form to create an advance for any employee, bypassing the 18th-of-month and concurrent-pending rules. HR step is auto-approved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs flex items-start gap-2">
              <Filter className="h-3.5 w-3.5 mt-0.5 text-blue-700" />
              <span>HR/Unit Manager/Accounts can create advance any day of month (bypasses 18th rule + concurrent-pending check). HR step auto-approved.</span>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Employee *</Label>
              <Select value={createForm.employee_id} onValueChange={(v) => setCreateForm({ ...createForm, employee_id: v })}>
                <SelectTrigger className="mt-1" data-testid="create-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} ({e.punch_id || "—"}) · {e.ctc ? fmtINR(e.ctc) : "no CTC"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Amount (₹) *</Label>
                <Input
                  type="number"
                  step="100"
                  value={createForm.amount}
                  onChange={(e) => setCreateForm({ ...createForm, amount: e.target.value })}
                  className="mt-1"
                  data-testid="create-amount-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">EMIs</Label>
                <Select value={String(createForm.repayment_months)} onValueChange={(v) => setCreateForm({ ...createForm, repayment_months: parseInt(v) })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{n} mo</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason Category *</Label>
              <Select value={createForm.reason_category} onValueChange={(v) => setCreateForm({ ...createForm, reason_category: v })}>
                <SelectTrigger className="mt-1" data-testid="create-reason-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {reasonOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Reason Description *</Label>
              <Textarea
                value={createForm.reason_description}
                onChange={(e) => setCreateForm({ ...createForm, reason_description: e.target.value })}
                rows={2}
                placeholder="Min 5 chars"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Payment Mode</Label>
                <Select value={createForm.payment_mode} onValueChange={(v) => setCreateForm({ ...createForm, payment_mode: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Bank">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-5">
                <input
                  type="checkbox"
                  id="override-max"
                  checked={createForm.override_max_check}
                  onChange={(e) => setCreateForm({ ...createForm, override_max_check: e.target.checked })}
                  data-testid="override-max-cb"
                />
                <label htmlFor="override-max" className="text-xs">Override 30% cap</label>
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Admin Notes (optional)</Label>
              <Input
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                placeholder="Internal note"
                className="mt-1"
              />
            </div>
            <div className="bg-blue-50/40 border border-blue-200 rounded p-2.5" data-testid="admin-deduction-month-section">
              <Label className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold">
                Deduction Start Month *
              </Label>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Input
                  type="month"
                  value={createForm.deduction_start_month}
                  onChange={(e) => setCreateForm({ ...createForm, deduction_start_month: e.target.value })}
                  className="max-w-[180px]"
                  data-testid="admin-deduction-month-input"
                />
                {(() => {
                  const chips = [];
                  const now = new Date();
                  for (let off = -2; off <= 2; off++) {
                    const d = new Date(now.getFullYear(), now.getMonth() + off, 1);
                    const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                    const lbl = d.toLocaleDateString("en-IN",
                      { month: "short", year: "2-digit" });
                    chips.push({ value: m, label: lbl });
                  }
                  return chips.map((c) => (
                    <button key={c.value} type="button"
                      onClick={() => setCreateForm({ ...createForm,
                                                       deduction_start_month: c.value })}
                      className={`text-[10px] px-1.5 py-0.5 rounded ring-1 ${
                        createForm.deduction_start_month === c.value
                          ? "bg-blue-600 text-white ring-blue-600"
                          : "bg-white ring-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`chip-${c.value}`}>
                      {c.label}
                    </button>
                  ));
                })()}
              </div>
              <p className="text-[10px] text-slate-600 mt-1">
                Default = <b>pending payroll month</b> ({pendingPayrollMonth}). Click a chip or pick directly to override.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              disabled={busy || !createForm.employee_id || !createForm.amount || !createForm.reason_category}
              onClick={async () => {
                setBusy(true);
                try {
                  const { data } = await api.post("/advances/admin-create", {
                    ...createForm,
                    amount: parseFloat(createForm.amount),
                  });
                  toast.success(`Created ${data.request_number} — ${data.overall_status === "pending_senior" ? "awaiting Senior Mgmt approval" : "ready for disbursement"}`);
                  setShowCreateDialog(false);
                  setCreateForm({
                    employee_id: "", amount: "", reason_category: "", reason_description: "",
                    repayment_months: 1, payment_mode: "Cash", notes: "", override_max_check: false,
                    deduction_start_month: (() => {
                      const d = new Date(); d.setMonth(d.getMonth() + 1);
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                    })(),
                  });
                  refresh();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
                } finally { setBusy(false); }
              }}
              className="brand-bg brand-bg-hover text-white"
              data-testid="create-submit-btn"
            >
              {busy ? "Creating…" : "Create Advance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Create Advances</DialogTitle>
            <DialogDescription className="sr-only">
              Paste rows from spreadsheet or add individually to create advances for multiple employees in one batch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!bulkResult ? (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                  Add multiple employees + amounts. Each row is validated independently — partial successes are saved.
                </div>
                <div className="bg-blue-50/40 border border-blue-200 rounded p-2 flex items-center gap-2 text-xs" data-testid="bulk-deduction-strip">
                  <Label className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold whitespace-nowrap">
                    Deduction Start Month (applies to all rows):
                  </Label>
                  <Input
                    type="month"
                    value={bulkDeductionMonth}
                    onChange={(e) => setBulkDeductionMonth(e.target.value)}
                    className="h-7 text-xs max-w-[160px]"
                    data-testid="bulk-deduction-month-input"
                  />
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr className="text-left">
                      <th className="px-2 py-1.5 font-semibold">Employee</th>
                      <th className="px-2 py-1.5 font-semibold">Amount</th>
                      <th className="px-2 py-1.5 font-semibold">EMIs</th>
                      <th className="px-2 py-1.5 font-semibold">Reason</th>
                      <th className="px-2 py-1.5 font-semibold">Description</th>
                      <th className="px-2 py-1.5 font-semibold">Mode</th>
                      <th className="px-2 py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((row, idx) => (
                      <tr key={idx} className="border-t border-slate-100" data-testid={`bulk-row-${idx}`}>
                        <td className="px-2 py-1 w-56">
                          <Select value={row.employee_id} onValueChange={(v) => {
                            const next = [...bulkRows]; next[idx] = { ...next[idx], employee_id: v }; setBulkRows(next);
                          }}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent className="max-h-[250px]">
                              {employees.map((e) => (
                                <SelectItem key={e.id} value={e.id}>{e.name} ({e.punch_id})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1 w-24">
                          <Input
                            type="number"
                            step="100"
                            value={row.amount}
                            onChange={(e) => {
                              const next = [...bulkRows]; next[idx] = { ...next[idx], amount: e.target.value }; setBulkRows(next);
                            }}
                            className="h-7 text-xs"
                          />
                        </td>
                        <td className="px-2 py-1 w-16">
                          <Select value={String(row.repayment_months)} onValueChange={(v) => {
                            const next = [...bulkRows]; next[idx] = { ...next[idx], repayment_months: parseInt(v) }; setBulkRows(next);
                          }}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{[1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1 w-40">
                          <Select value={row.reason_category} onValueChange={(v) => {
                            const next = [...bulkRows]; next[idx] = { ...next[idx], reason_category: v }; setBulkRows(next);
                          }}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>{reasonOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            value={row.reason_description}
                            onChange={(e) => {
                              const next = [...bulkRows]; next[idx] = { ...next[idx], reason_description: e.target.value }; setBulkRows(next);
                            }}
                            placeholder="Min 5 chars"
                            className="h-7 text-xs"
                          />
                        </td>
                        <td className="px-2 py-1 w-20">
                          <Select value={row.payment_mode} onValueChange={(v) => {
                            const next = [...bulkRows]; next[idx] = { ...next[idx], payment_mode: v }; setBulkRows(next);
                          }}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="Bank">Bank</SelectItem></SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1 w-10">
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => setBulkRows(bulkRows.filter((_, i) => i !== idx))} disabled={bulkRows.length === 1}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkRows([...bulkRows, { employee_id: "", amount: "", reason_category: "", reason_description: "", repayment_months: 1, payment_mode: "Cash" }])}
                  data-testid="add-bulk-row-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Row
                </Button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-[10px] text-slate-500">Total</div>
                    <div className="text-xl font-bold">{bulkResult.total_submitted}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                    <div className="text-[10px] text-emerald-700">Created</div>
                    <div className="text-xl font-bold text-emerald-700">{bulkResult.created_count}</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded p-2">
                    <div className="text-[10px] text-red-700">Failed</div>
                    <div className="text-xl font-bold text-red-700">{bulkResult.failed_count}</div>
                  </div>
                </div>
                {bulkResult.created.length > 0 && (
                  <details open>
                    <summary className="cursor-pointer text-xs font-bold text-emerald-700">✅ Created ({bulkResult.created.length})</summary>
                    <table className="w-full text-xs mt-1">
                      <tbody>
                        {bulkResult.created.map((r) => (
                          <tr key={r.request_number} className="border-t border-slate-100">
                            <td className="px-2 py-1 font-mono text-[10px]">{r.request_number}</td>
                            <td className="px-2 py-1">{r.employee_name}</td>
                            <td className="px-2 py-1 text-right">{fmtINR(r.amount)}</td>
                            <td className="px-2 py-1 text-[10px]">{r.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
                {bulkResult.failed.length > 0 && (
                  <details open>
                    <summary className="cursor-pointer text-xs font-bold text-red-700">❌ Failed ({bulkResult.failed.length})</summary>
                    <table className="w-full text-xs mt-1">
                      <tbody>
                        {bulkResult.failed.map((r, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-2 py-1 font-mono text-[10px]">{r.employee_id}</td>
                            <td className="px-2 py-1 text-right">{fmtINR(r.amount)}</td>
                            <td className="px-2 py-1 text-red-600 text-[10px]">{r.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {!bulkResult ? (
              <>
                <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
                <Button
                  onClick={async () => {
                    const items = bulkRows
                      .filter((r) => r.employee_id && r.amount && r.reason_category && (r.reason_description || "").length >= 5)
                      .map((r) => ({ ...r, amount: parseFloat(r.amount), deduction_start_month: bulkDeductionMonth || null }));
                    if (items.length === 0) {
                      toast.error("Add at least one valid row (employee + amount + reason + description ≥ 5 chars)");
                      return;
                    }
                    setBusy(true);
                    try {
                      const { data } = await api.post("/advances/admin-bulk-create", { items });
                      setBulkResult(data);
                      if (data.created_count > 0) toast.success(`Created ${data.created_count} advance(s)`);
                      if (data.failed_count > 0) toast.error(`${data.failed_count} failed — see results`);
                      refresh();
                    } catch (e) {
                      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk failed");
                    } finally { setBusy(false); }
                  }}
                  disabled={busy}
                  className="brand-bg brand-bg-hover text-white"
                  data-testid="bulk-submit-btn"
                >
                  {busy ? "Processing…" : `Create ${bulkRows.filter((r) => r.employee_id && r.amount && r.reason_category).length} Advance(s)`}
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowBulkDialog(false)} className="brand-bg brand-bg-hover text-white">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
